import { normalizeCurrencyBaseUnitAmount } from '@aeppic/shared/currencies'

export interface CurrencyAmount {
  amount: number
  currency?: string
  precision: number
}

export type RoundingMode = 'half-up' | 'half-even' | 'down'

export interface RoundOptions {
  precision: number
  rounding?: RoundingMode
}

export interface MultiplyOptions extends Partial<RoundOptions> {
  currency?: string
}

export interface LineItemOptions extends MultiplyOptions {
  quantity?: number | string
}

export interface SumOptions {
  currency?: string
  precision?: number
  rounding?: RoundingMode
}

export type VatRate = number | string

export interface AddProductOptions extends LineItemOptions {
  linePrecision?: number
}

export interface InvoiceOptions extends SumOptions {
  vatRates?: Record<string, VatRate>
}

export interface InvoiceRowInput {
  id: string
  amount: CurrencyAmount
  quantity?: number | string
  vatType?: string
  precision?: number
  rounding?: RoundingMode
}

export interface InvoiceRowResult {
  id: string
  net: CurrencyAmount
  vatType?: string
}

export interface InvoicePageSnapshot {
  rows: InvoiceRowResult[]
  netSubtotal: CurrencyAmount
}

export interface InvoiceVatGroupSummary {
  vatType: string
  rate: VatRate
  net: CurrencyAmount
  vat: CurrencyAmount
  gross: CurrencyAmount
}

export interface InvoiceSummary {
  rows: InvoiceRowResult[]
  netTotal: CurrencyAmount
  vatGroups: Record<string, InvoiceVatGroupSummary>
  vatTotal: CurrencyAmount
  grossTotal: CurrencyAmount
}

export interface FinancialAccumulator {
  add(value: CurrencyAmount): FinancialAccumulator
  addAmount(amount: number, precision: number, options?: { currency?: string }): FinancialAccumulator
  addProduct(value: CurrencyAmount & { quantity: number | string }, options?: AddProductOptions): FinancialAccumulator
  snapshot(options?: Partial<RoundOptions>): CurrencyAmount
  valueOf(): number
}

export interface InvoiceCalculator {
  addRow(row: InvoiceRowInput): InvoiceRowResult
  closePage(): InvoicePageSnapshot
  page(): InvoicePageSnapshot
  summary(): InvoiceSummary
}

export interface FinancialAPI {
  amount(amount: number, precision: number, options?: { currency?: string }): CurrencyAmount
  round(value: CurrencyAmount, options: RoundOptions): CurrencyAmount
  multiply(value: CurrencyAmount, quantity: number | string, options?: MultiplyOptions): CurrencyAmount
  line(value: CurrencyAmount, options?: LineItemOptions): CurrencyAmount
  add(values: CurrencyAmount[], options?: SumOptions): CurrencyAmount
  sum(options?: SumOptions): FinancialAccumulator
  invoice(options?: InvoiceOptions): InvoiceCalculator
}

interface MutableCurrencyAmount {
  amount: bigint
  currency?: string
  precision: number
}

const DEFAULT_ROUNDING: RoundingMode = 'half-up'

function assertSafeInteger(value: bigint): number {
  const numberValue = Number(value)
  if (!Number.isSafeInteger(numberValue)) {
    throw new Error(`Financial amount exceeds JavaScript safe integer range: ${value.toString()}`)
  }
  return numberValue
}

function assertPrecision(precision: number): number {
  if (!Number.isInteger(precision) || precision < 0) {
    throw new Error(`Invalid currency precision: ${precision}`)
  }
  return precision
}

function assertAmount(amount: number): number {
  const normalizedAmount = normalizeCurrencyBaseUnitAmount(amount, {
    context: 'Aeppic.Financial',
  })

  if (!Number.isSafeInteger(normalizedAmount)) {
    throw new Error(`Currency amount must be a safe integer in base units: ${amount}`)
  }
  return normalizedAmount
}

function powerOfTen(exponent: number): bigint {
  assertPrecision(exponent)
  return BigInt(10) ** BigInt(exponent)
}

function normalizeCurrency(currency: string | undefined, expected: string | undefined) {
  if (currency && expected && currency !== expected) {
    throw new Error(`Cannot combine different currencies: ${expected} and ${currency}`)
  }
  return expected ?? currency
}

function toMutable(value: CurrencyAmount): MutableCurrencyAmount {
  return {
    amount: BigInt(assertAmount(value.amount)),
    currency: value.currency,
    precision: assertPrecision(value.precision),
  }
}

function toCurrencyAmount(value: MutableCurrencyAmount): CurrencyAmount {
  return {
    amount: assertSafeInteger(value.amount),
    currency: value.currency,
    precision: value.precision,
  }
}

function roundDivision(numerator: bigint, denominator: bigint, rounding: RoundingMode = DEFAULT_ROUNDING): bigint {
  if (denominator <= 0n) {
    throw new Error('Financial division denominator must be positive')
  }

  const negative = numerator < 0n
  const absolute = negative ? -numerator : numerator
  let quotient = absolute / denominator
  const remainder = absolute % denominator

  if (rounding === 'half-up') {
    if (remainder * 2n >= denominator) {
      quotient += 1n
    }
  } else if (rounding === 'half-even') {
    const doubledRemainder = remainder * 2n
    if (doubledRemainder > denominator || (doubledRemainder === denominator && quotient % 2n === 1n)) {
      quotient += 1n
    }
  } else if (rounding !== 'down') {
    throw new Error(`Unsupported financial rounding mode: ${rounding}`)
  }

  return negative ? -quotient : quotient
}

function rescale(value: MutableCurrencyAmount, precision: number, rounding: RoundingMode = DEFAULT_ROUNDING): MutableCurrencyAmount {
  precision = assertPrecision(precision)

  if (value.precision === precision) {
    return { ...value }
  }

  if (value.precision < precision) {
    return {
      ...value,
      amount: value.amount * powerOfTen(precision - value.precision),
      precision,
    }
  }

  return {
    ...value,
    amount: roundDivision(value.amount, powerOfTen(value.precision - precision), rounding),
    precision,
  }
}

function decimalToRational(value: number | string): { numerator: bigint, denominator: bigint } {
  const source = typeof value === 'number' ? value.toString() : value.trim()
  const match = source.match(/^([+-])?(\d+)(?:\.(\d+))?$/)

  if (!match) {
    throw new Error(`Invalid decimal quantity: ${value}`)
  }

  const sign = match[1] === '-' ? -1n : 1n
  const integerPart = match[2]
  const fractionPart = match[3] ?? ''
  const denominator = powerOfTen(fractionPart.length)
  const numerator = BigInt(`${integerPart}${fractionPart || ''}`) * sign

  return { numerator, denominator }
}

function cloneCurrencyAmount(value: CurrencyAmount): CurrencyAmount {
  return {
    amount: value.amount,
    currency: value.currency,
    precision: value.precision,
  }
}

export function createFinancialAPI(): FinancialAPI {
  function amount(baseAmount: number, precision: number, options: { currency?: string } = {}): CurrencyAmount {
    return {
      amount: assertAmount(baseAmount),
      currency: options.currency,
      precision: assertPrecision(precision),
    }
  }

  function round(value: CurrencyAmount, options: RoundOptions): CurrencyAmount {
    return toCurrencyAmount(rescale(toMutable(value), options.precision, options.rounding))
  }

  function multiply(value: CurrencyAmount, quantity: number | string, options: MultiplyOptions = {}): CurrencyAmount {
    const input = toMutable(value)
    const outputPrecision = options.precision ?? input.precision
    const { numerator, denominator } = decimalToRational(quantity)
    const precisionDelta = outputPrecision - input.precision
    const scaledNumerator = precisionDelta >= 0
      ? input.amount * numerator * powerOfTen(precisionDelta)
      : input.amount * numerator
    const scaledDenominator = precisionDelta >= 0
      ? denominator
      : denominator * powerOfTen(-precisionDelta)

    return toCurrencyAmount({
      amount: roundDivision(scaledNumerator, scaledDenominator, options.rounding),
      currency: normalizeCurrency(options.currency, input.currency),
      precision: outputPrecision,
    })
  }

  function line(value: CurrencyAmount, options: LineItemOptions = {}): CurrencyAmount {
    return multiply(value, options.quantity ?? 1, {
      currency: options.currency,
      precision: options.precision,
      rounding: options.rounding,
    })
  }

  function add(values: CurrencyAmount[], options: SumOptions = {}): CurrencyAmount {
    const accumulator = sum(options)
    for (const value of values) {
      accumulator.add(value)
    }
    return accumulator.snapshot()
  }

  function sum(options: SumOptions = {}): FinancialAccumulator {
    let total = 0n
    let precision = options.precision
    let currency = options.currency
    const fixedPrecision = precision != null
    const rounding = options.rounding ?? DEFAULT_ROUNDING

    const accumulator: FinancialAccumulator = {
      add(value: CurrencyAmount) {
        const incoming = toMutable(value)
        currency = normalizeCurrency(incoming.currency, currency)

        if (precision == null) {
          precision = incoming.precision
        }

        if (!fixedPrecision && incoming.precision > precision) {
          total *= powerOfTen(incoming.precision - precision)
          precision = incoming.precision
        }

        total += rescale(incoming, precision, rounding).amount
        return accumulator
      },

      addAmount(baseAmount: number, valuePrecision: number, valueOptions: { currency?: string } = {}) {
        return accumulator.add(amount(baseAmount, valuePrecision, valueOptions))
      },

      addProduct(value: CurrencyAmount & { quantity: number | string }, valueOptions: AddProductOptions = {}) {
        const linePrecision = valueOptions.linePrecision ?? valueOptions.precision ?? precision ?? value.precision
        return accumulator.add(multiply(value, valueOptions.quantity ?? value.quantity, {
          currency: valueOptions.currency,
          precision: linePrecision,
          rounding: valueOptions.rounding ?? rounding,
        }))
      },

      snapshot(snapshotOptions: Partial<RoundOptions> = {}) {
        const value = {
          amount: total,
          currency,
          precision: precision ?? 0,
        }

        if (snapshotOptions.precision == null || snapshotOptions.precision === value.precision) {
          return toCurrencyAmount(value)
        }

        return toCurrencyAmount(rescale(value, snapshotOptions.precision, snapshotOptions.rounding ?? rounding))
      },

      valueOf() {
        return accumulator.snapshot().amount
      },
    }

    return accumulator
  }

  function invoice(options: InvoiceOptions = {}): InvoiceCalculator {
    const precision = options.precision
    const currency = options.currency
    const rounding = options.rounding ?? DEFAULT_ROUNDING
    const vatRates = options.vatRates ?? {}
    const rows: InvoiceRowResult[] = []
    let pageRows: InvoiceRowResult[] = []
    const totalNet = sum({ currency, precision, rounding })
    let pageNet = sum({ currency, precision, rounding })
    const netByVatType = new Map<string, FinancialAccumulator>()

    function getVatGroup(vatType: string) {
      let group = netByVatType.get(vatType)
      if (!group) {
        group = sum({ currency, precision, rounding })
        netByVatType.set(vatType, group)
      }
      return group
    }

    function snapshotRows(source: InvoiceRowResult[]) {
      return source.map(row => ({
        id: row.id,
        net: cloneCurrencyAmount(row.net),
        vatType: row.vatType,
      }))
    }

    function getOutputPrecision(value: CurrencyAmount) {
      return precision ?? value.precision
    }

    function addRow(row: InvoiceRowInput): InvoiceRowResult {
      const net = line(row.amount, {
        quantity: row.quantity ?? 1,
        precision: row.precision ?? getOutputPrecision(row.amount),
        rounding: row.rounding ?? rounding,
        currency,
      })
      const result: InvoiceRowResult = {
        id: row.id,
        net,
        vatType: row.vatType,
      }

      rows.push(result)
      pageRows.push(result)
      totalNet.add(net)
      pageNet.add(net)

      if (row.vatType) {
        getVatGroup(row.vatType).add(net)
      }

      return {
        id: result.id,
        net: cloneCurrencyAmount(result.net),
        vatType: result.vatType,
      }
    }

    function page(): InvoicePageSnapshot {
      return {
        rows: snapshotRows(pageRows),
        netSubtotal: pageNet.snapshot(),
      }
    }

    function closePage(): InvoicePageSnapshot {
      const snapshot = page()
      pageRows = []
      pageNet = sum({ currency, precision, rounding })
      return snapshot
    }

    function summary(): InvoiceSummary {
      const netTotal = totalNet.snapshot()
      const vatTotal = sum({ currency: netTotal.currency, precision: netTotal.precision, rounding })
      const grossTotal = sum({ currency: netTotal.currency, precision: netTotal.precision, rounding })
      const vatGroups: Record<string, InvoiceVatGroupSummary> = {}

      grossTotal.add(netTotal)

      for (const [vatType, netAccumulator] of netByVatType.entries()) {
        const net = netAccumulator.snapshot()
        const rate = vatRates[vatType] ?? 0
        const vat = multiply(net, rate, {
          currency: net.currency,
          precision: net.precision,
          rounding,
        })
        const gross = add([net, vat], {
          currency: net.currency,
          precision: net.precision,
          rounding,
        })

        vatTotal.add(vat)
        grossTotal.add(vat)

        vatGroups[vatType] = {
          vatType,
          rate,
          net,
          vat,
          gross,
        }
      }

      return {
        rows: snapshotRows(rows),
        netTotal,
        vatGroups,
        vatTotal: vatTotal.snapshot(),
        grossTotal: grossTotal.snapshot(),
      }
    }

    return {
      addRow,
      closePage,
      page,
      summary,
    }
  }

  return {
    amount,
    round,
    multiply,
    line,
    add,
    sum,
    invoice,
  }
}
